import { useState } from 'react';
import {
  useAdressesPassees,
  useChangerLeMotDePasse,
  useConnexions,
  useDemanderLAdresse,
  useFermerLesConnexions,
  usePoserUnePreference,
  useRattachements,
  useReglages,
  useRemettreLesExceptions,
} from '../features/reglages/reglages';
import { lireLErreur } from '../shared/api/client';
import { Bandeau } from '../shared/ui/Bandeau';
import { Bouton } from '../shared/ui/Bouton';
import { Carte } from '../shared/ui/Carte';
import { Saisie } from '../shared/ui/Champ';
import { EtatErreur } from '../shared/ui/EtatErreur';
import { Interrupteur } from '../shared/ui/Interrupteur';
import { Squelette } from '../shared/ui/Squelette';
import { Statut } from '../shared/ui/Statut';
import { Titre } from '../shared/ui/Titre';
import { dateLisible, instantLisible } from '../shared/format/duree';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES RÉGLAGES — `CDC-4` F-51 ET F-52.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **L'écran existe pour les QUATRE rôles, et il n'est pas le même pour tous** (Q481).
 * **Ce n'est pas cette page qui en décide** : le serveur rend `etendue: 'reduite'` aux deux
 * rôles qui administrent — « il porte l'adresse, le mot de passe et les connexions en
 * cours, **et rien d'autre** : ils n'ont ni cours, ni espace de fichiers, ni conversation,
 * donc les trois préférences n'auraient aucun objet ». Le compilateur refuse de lire les
 * préférences sans avoir testé le discriminant, donc **l'oubli est impossible**.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CET ÉCRAN NE PORTE PAS, ET CHAQUE ABSENCE EST ÉCRITE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * - **Aucun réglage de langue** (Q494) : « français seul, **aucune colonne de langue dans
 *   le modèle** ».
 * - **Aucune liste des conversations qui portent une exception** : « lister les
 *   conversations ici referait la surface de l'annuaire une seconde fois ». Seulement leur
 *   **nombre**, et un geste unique pour toutes les remettre.
 * - **Aucun retrait d'adresse passée** : le refus porte `ADRESSE_PASSEE_NON_RETIRABLE`, et
 *   le produit ne montre donc pas un geste qu'il refuse.
 * - **Aucune fermeture de connexion ligne par ligne** (F-52), et c'est une décision.
 * - **Aucun export de ses propres données** : « c'est le seul endroit où elle peut les
 *   regarder, **aucun export n'existant en V1** ».
 * - **« Supprimer mon compte » n'y est pas encore** : F-53 l'attache à l'anonymisation de
 *   `CDC-1` F-11, et « l'écran nomme ce qui est détruit AVANT de le détruire, fait par
 *   fait ». Cette surface-là est un écran à part entière, et elle vient avec son lot.
 */
export function PageReglages() {
  const reglages = useReglages();

  return (
    <main className="mx-auto flex w-full max-w-etroite flex-col gap-6 p-6">
      <Titre niveau={1}>Réglages</Titre>

      {reglages.isLoading && <Squelette lignes={8} />}

      {reglages.isError && (
        <EtatErreur
          message={lireLErreur(reglages.error).message}
          onReessayer={() => void reglages.refetch()}
        />
      )}

      {reglages.data !== undefined && (
        <>
          <BlocAdresse
            valeur={reglages.data.adresse.valeur}
            prouvee={reglages.data.adresse.prouvee}
          />

          <BlocMotDePasse />

          {/* **Le discriminant décide, pas un rôle.** Une page qui testerait
              `role === 'TUTOR'` dupliquerait une décision que le serveur a déjà prise, et
              les deux divergeraient au premier rôle ajouté. */}
          {reglages.data.etendue === 'complete' && (
            <>
              <BlocPreferences
                media={reglages.data.preferences.media}
                rangement={reglages.data.preferences.rangement}
                notification={reglages.data.preferences.notification}
                exceptions={reglages.data.exceptionsDeNotification}
              />
              <BlocRattachements />
            </>
          )}

          <BlocAdressesPassees />
          <BlocConnexions />
        </>
      )}
    </main>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   L'adresse email — deux étapes, et la première ne change rien
   ═════════════════════════════════════════════════════════════════════════ */

function BlocAdresse({
  valeur,
  prouvee,
}: {
  readonly valeur: string | null;
  readonly prouvee: boolean;
}) {
  const [motDePasseCourant, poserMotDePasse] = useState('');
  const [nouvelleAdresse, poserAdresse] = useState('');
  const demande = useDemanderLAdresse();

  return (
    <Carte>
      <div className="flex flex-col gap-4">
        <Titre niveau={2} taille="18">
          Adresse email
        </Titre>

        <p className="text-14 text-texte">{valeur ?? '—'}</p>

        {!prouvee && (
          // **Le refus nomme le chemin qui le lève** (Q550), et c'est la règle : « le
          // changement est REFUSÉ tant que l'adresse courante n'a jamais été prouvée ».
          // Le produit le dit **avant** le refus plutôt qu'après, parce qu'un refus après
          // saisie fait recommencer la personne sans lui apprendre autre chose.
          <Bandeau ton="attention">
            Votre adresse n’a jamais été prouvée&nbsp;: le changement est refusé
            tant qu’elle ne l’est pas. Passez une fois par «&nbsp;mot de passe
            oublié&nbsp;» pour la prouver.
          </Bandeau>
        )}

        {demande.isSuccess ? (
          // « **Tant que le code n'est pas saisi, l'adresse du compte ne change pas.** »
          // Le dire est une obligation de cet écran : sans cette phrase, une personne
          // croit le changement fait et cesse de surveiller sa boîte.
          <Bandeau ton="positif">
            Un code est parti vers votre nouvelle adresse. Votre adresse n’a pas
            encore changé&nbsp;: elle ne changera qu’une fois ce code saisi. Une
            notification est aussi partie vers l’adresse actuelle.
          </Bandeau>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(evenement) => {
              evenement.preventDefault();
              demande.mutate({ motDePasseCourant, nouvelleAdresse });
            }}
          >
            <Saisie
              libelle="Mot de passe courant"
              type="password"
              autoComplete="current-password"
              value={motDePasseCourant}
              onChange={(evenement) => poserMotDePasse(evenement.target.value)}
            />
            <Saisie
              libelle="Nouvelle adresse"
              type="email"
              autoComplete="email"
              value={nouvelleAdresse}
              onChange={(evenement) => poserAdresse(evenement.target.value)}
            />
            {demande.isError && (
              <Bandeau ton="echec">{lireLErreur(demande.error).message}</Bandeau>
            )}
            <span>
              <Bouton
                type="submit"
                enCours={demande.isPending}
                disabled={!prouvee}
              >
                Demander le changement
              </Bouton>
            </span>
          </form>
        )}
      </div>
    </Carte>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   Le mot de passe
   ═════════════════════════════════════════════════════════════════════════ */

function BlocMotDePasse() {
  const [courant, poserCourant] = useState('');
  const [nouveau, poserNouveau] = useState('');
  const changement = useChangerLeMotDePasse();

  return (
    <Carte>
      <div className="flex flex-col gap-4">
        <Titre niveau={2} taille="18">
          Mot de passe
        </Titre>

        {changement.isSuccess ? (
          <Bandeau ton="positif">
            Votre mot de passe est changé. Un email de confirmation est parti.
          </Bandeau>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(evenement) => {
              evenement.preventDefault();
              changement.mutate({
                motDePasseCourant: courant,
                nouveauMotDePasse: nouveau,
              });
            }}
          >
            <Saisie
              libelle="Mot de passe courant"
              type="password"
              autoComplete="current-password"
              value={courant}
              onChange={(evenement) => poserCourant(evenement.target.value)}
            />
            <Saisie
              libelle="Nouveau mot de passe"
              type="password"
              autoComplete="new-password"
              value={nouveau}
              onChange={(evenement) => poserNouveau(evenement.target.value)}
            />
            {changement.isError && (
              <Bandeau ton="echec">
                {lireLErreur(changement.error).message}
              </Bandeau>
            )}
            <span>
              <Bouton type="submit" enCours={changement.isPending}>
                Changer le mot de passe
              </Bouton>
            </span>
          </form>
        )}
      </div>
    </Carte>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   Les trois préférences — F-51
   ═════════════════════════════════════════════════════════════════════════ */

function BlocPreferences({
  media,
  rangement,
  notification,
  exceptions,
}: {
  readonly media: { readonly micro: boolean; readonly camera: boolean };
  readonly rangement: { readonly dossierId: string | null };
  readonly notification: { readonly emailDeConversation: boolean };
  readonly exceptions: number;
}) {
  const poser = usePoserUnePreference();
  const remise = useRemettreLesExceptions();

  return (
    <Carte>
      <div className="flex flex-col gap-5">
        <Titre niveau={2} taille="18">
          Préférences
        </Titre>

        {/* « **Les trois s'affichent et se règlent depuis cet écran, EN PLUS du geste qui
            continue de valoir** » (Q493) : sans écran, il faudrait entrer dans un cours
            pour changer sa caméra. « Le geste reste le chemin court, **l'écran est le
            chemin qui existe hors de l'occasion**. » */}
        <Interrupteur
          libelle="Micro ouvert en entrant dans un cours"
          aide="Le couper ou le rallumer pendant un cours met aussi ce réglage à jour. Une coupure imposée par le tuteur, non."
          actif={media.micro}
          enCours={poser.isPending}
          onChanger={(actif) =>
            poser.mutate({ nom: 'media', valeur: { micro: actif } })
          }
        />

        <Interrupteur
          libelle="Caméra allumée en entrant dans un cours"
          actif={media.camera}
          enCours={poser.isPending}
          onChanger={(actif) =>
            poser.mutate({ nom: 'media', valeur: { camera: actif } })
          }
        />

        <Interrupteur
          libelle="Recevoir un email à la réception d’un message"
          aide="L’exception d’une conversation se règle dans la conversation, pas ici."
          actif={notification.emailDeConversation}
          enCours={poser.isPending}
          onChanger={(actif) =>
            poser.mutate({
              nom: 'notification',
              valeur: { emailDeConversation: actif },
            })
          }
        />

        {/* **Le nombre, et le geste unique — jamais la liste.** « Sans quoi un fil coupé
            six mois plus tôt ne se retrouve plus. » */}
        {exceptions > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-bord pt-4">
            <p className="text-14 text-texte">
              {exceptions} conversation{exceptions > 1 ? 's portent' : ' porte'}{' '}
              une exception à ce réglage.
            </p>
            <Bouton
              variante="douce"
              taille="petite"
              enCours={remise.isPending}
              onClick={() => remise.mutate()}
            >
              Toutes les remettre au réglage général
            </Bouton>
          </div>
        )}

        {/* **Le rangement se lit ici, et il se CHOISIT dans l'espace de fichiers**, où les
            dossiers existent. Montrer un sélecteur de dossier ici demanderait de charger
            l'arborescence entière d'un espace pour un réglage qu'on touche deux fois
            l'an. */}
        <div className="flex items-start justify-between gap-4 border-t border-bord pt-4">
          <span className="flex flex-col gap-1">
            <span className="text-14 font-weight-700 text-texte-fort">
              Où se range un document fabriqué
            </span>
            <span className="text-13 text-texte-faible">
              Se choisit depuis un dossier de votre espace de fichiers.
            </span>
          </span>
          <Statut teinte="neutre" pleine={false}>
            {rangement.dossierId === null ? 'Par défaut' : 'Un dossier choisi'}
          </Statut>
        </div>
      </div>
    </Carte>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   Les rattachements — actifs ET terminés
   ═════════════════════════════════════════════════════════════════════════ */

function BlocRattachements() {
  const rattachements = useRattachements();

  return (
    <Carte>
      <div className="flex flex-col gap-4">
        <Titre niveau={2} taille="18">
          Vos rattachements
        </Titre>

        {/* « Ils n'ont **aucune autre surface**, et ils conditionnent tout l'accès : **les
            lui montrer est ce qui rend explicable l'espace qui ne peut rien**. » */}
        <p className="text-13 text-texte-faible">
          Vos rattachements décident de ce que vous pouvez faire. Sans
          rattachement actif, vous gardez votre historique et vos fichiers, et
          aucun cours n’est possible.
        </p>

        {rattachements.isLoading && <Squelette lignes={2} />}

        {rattachements.isError && (
          <EtatErreur
            message={lireLErreur(rattachements.error).message}
            onReessayer={() => void rattachements.refetch()}
          />
        )}

        {rattachements.data !== undefined && (
          <ul className="flex flex-col gap-3">
            {rattachements.data.rattachements.map((lien) => (
              <li
                key={`${lien.structure}-${lien.depuis}`}
                className="flex flex-wrap items-center justify-between gap-3"
              >
                <span className="text-14 text-texte">{lien.structure}</span>
                <span className="flex items-center gap-3">
                  <span className="text-13 text-texte-faible">
                    Depuis le {dateLisible(lien.depuis)}
                    {lien.jusqua !== null &&
                      ` · terminé le ${dateLisible(lien.jusqua)}`}
                  </span>
                  <Statut teinte={lien.jusqua === null ? 'positif' : 'eteint'}>
                    {lien.jusqua === null ? 'Actif' : 'Terminé'}
                  </Statut>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Carte>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   Les adresses passées — en lecture, et elles ne se retirent pas
   ═════════════════════════════════════════════════════════════════════════ */

function BlocAdressesPassees() {
  const adresses = useAdressesPassees();

  if (adresses.data !== undefined && adresses.data.adresses.length === 0) {
    // **Aucun état vide décoratif ici.** Une personne qui n'a jamais changé d'adresse n'a
    // rien à apprendre d'un encadré qui le lui dit : le bloc n'existe pas.
    return null;
  }

  return (
    <Carte>
      <div className="flex flex-col gap-4">
        <Titre niveau={2} taille="18">
          Vos adresses passées
        </Titre>

        {/* « Elle n'en retire aucune : le produit les garde pour **continuer à la
            reconnaître** quand une structure la provisionne avec l'adresse qu'elle
            connaît. **En retirer une casserait cette reconnaissance en silence.** » */}
        <p className="text-13 text-texte-faible">
          Elles se conservent pour vous reconnaître quand une structure vous
          provisionne avec une adresse que vous n’utilisez plus. Elles ne se
          retirent pas.
        </p>

        {adresses.isLoading && <Squelette lignes={2} />}

        {adresses.isError && (
          <EtatErreur
            message={lireLErreur(adresses.error).message}
            onReessayer={() => void adresses.refetch()}
          />
        )}

        {adresses.data !== undefined && (
          <ul className="flex flex-col gap-2">
            {adresses.data.adresses.map((ancienne) => (
              <li
                key={`${ancienne.valeur}-${ancienne.changeeLe}`}
                className="flex flex-wrap items-baseline justify-between gap-3"
              >
                <span className="text-14 text-texte">{ancienne.valeur}</span>
                <time
                  dateTime={ancienne.changeeLe}
                  className="text-13 text-texte-faible"
                >
                  Changée le {dateLisible(ancienne.changeeLe)}
                </time>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Carte>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   Les connexions en cours — F-52
   ═════════════════════════════════════════════════════════════════════════ */

function BlocConnexions() {
  const connexions = useConnexions();
  const fermeture = useFermerLesConnexions();
  const [motDePasseCourant, poserMotDePasse] = useState('');

  return (
    <Carte>
      <div className="flex flex-col gap-4">
        <Titre niveau={2} taille="18">
          Vos connexions en cours
        </Titre>

        {connexions.isLoading && <Squelette lignes={3} />}

        {connexions.isError && (
          <EtatErreur
            message={lireLErreur(connexions.error).message}
            onReessayer={() => void connexions.refetch()}
          />
        )}

        {connexions.data !== undefined && (
          <ul className="flex flex-col gap-3">
            {connexions.data.connexions.map((connexion) => (
              <li
                key={connexion.id}
                className="flex flex-wrap items-center justify-between gap-3"
              >
                <span className="flex flex-col">
                  <span className="text-14 text-texte">
                    {connexion.appareil ?? 'Appareil inconnu'}
                  </span>
                  <span className="text-13 text-texte-faible">
                    Depuis le {instantLisible(connexion.depuis)}
                    {connexion.adresseReseau !== null &&
                      ` · ${connexion.adresseReseau}`}
                  </span>
                </span>
                {connexion.cestVous && (
                  <Statut teinte="positif">Cet appareil</Statut>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* **Un seul geste, et il exige le mot de passe courant.** « Il n'y a pas de
            fermeture ligne par ligne, **et c'est une décision** : distinguer une connexion
            étrangère d'une des siennes sur une adresse réseau et un nom d'appareil n'est
            pas quelque chose qu'une personne sait faire. » */}
        <form
          className="flex flex-col gap-3 border-t border-bord pt-4"
          onSubmit={(evenement) => {
            evenement.preventDefault();
            fermeture.mutate({ motDePasseCourant });
          }}
        >
          <p className="text-13 text-texte-faible">
            Fermer toutes vos connexions sauf celle-ci. Si vous étiez dans un
            cours ailleurs, vous en sortez.
          </p>
          <Saisie
            libelle="Mot de passe courant"
            type="password"
            autoComplete="current-password"
            value={motDePasseCourant}
            onChange={(evenement) => poserMotDePasse(evenement.target.value)}
          />
          {fermeture.isError && (
            <Bandeau ton="echec">{lireLErreur(fermeture.error).message}</Bandeau>
          )}
          {fermeture.isSuccess && (
            <Bandeau ton="positif">
              {fermeture.data.sessionsFermees} connexion
              {fermeture.data.sessionsFermees > 1 ? 's fermées' : ' fermée'}.
            </Bandeau>
          )}
          <span>
            {/* **Aucune variante rouge, et c'est délibéré** : la charte ne porte
                AUCUN ton destructif — le rouge y est celui d'un ÉCHEC, pas celui d'un
                geste voulu. En inventer un ici serait redécider la charte en écrivant
                un écran, ce que `design-system-first` ferme. **Ce qui pèse ce geste est
                le mot de passe qu'il exige**, pas une couleur. Point porté à la table
                « À valider » de `DESIGN.md`. */}
            <Bouton type="submit" variante="douce" enCours={fermeture.isPending}>
              Fermer mes autres connexions
            </Bouton>
          </span>
        </form>
      </div>
    </Carte>
  );
}
